import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import './Level3.css';
import { unlockNextLevel } from './Levelselect';

import Preloader from '../components/layout/Preloader';
import Header from '../components/layout/Header';
import PausePopup from '../components/layout/Pausepopup';
import WinPopup from '../components/layout/Winpopup';
import LosePopup from '../components/layout/Losepopup';
import level6Bg from '../asserts/level1bg.png';
import leftChar from '../asserts/avatar.png';
import rightChar from '../asserts/av6.png';
import fragmentImg from '../asserts/fragment6.png';
import cardBack from '../asserts/cardback.png';
import heartCard from '../asserts/heartcard.png';
import card2 from '../asserts/card2.png';
import card3 from '../asserts/card3.png';
import card4 from '../asserts/card4.png';
import card5 from '../asserts/card5.png';
import card6 from '../asserts/card6.png';
import card7 from '../asserts/card7.png';
import card8 from '../asserts/card8.png';
import card9 from '../asserts/card9.png';
import task2card1 from '../asserts/task2card1.png';
import task2card2 from '../asserts/task2card2.png';
import task2card3 from '../asserts/task2card3.png';

const CHOOSE_SECONDS = 18;

const DIALOGUES = [
  { side: 'left',  text: '"Give me the sixth fragment. What must I do?"' },
  { side: 'right', text: '"Track the heart through five cards. Prove your memory."' },
];

const ALL_EXTRA_CARDS = [card2, card3, card4, card5, card6, card7, card8, card9];

function pickRandomCards(count) {
  const shuffled = [...ALL_EXTRA_CARDS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

const ALL_TASK2_DATA = [
  {
    question: '"If you remember — which of the cards was the Pharaoh\'s case?"',
    hint: 'Hint: A case means protect.',
    answers: [
      { img: task2card1, correct: false },
      { img: task2card2, correct: false },
      { img: task2card3, correct: true  },
    ],
  },
  {
    question: '"The Pharaoh guards one secret — which card bore his seal?"',
    hint: 'Hint: A seal marks the true one.',
    answers: [
      { img: task2card2, correct: false },
      { img: task2card3, correct: false },
      { img: task2card1, correct: true  },
    ],
  },
  {
    question: '"Only one card holds the Pharaoh\'s blessing. Which was it?"',
    hint: 'Hint: The blessed card shines brightest.',
    answers: [
      { img: task2card3, correct: false },
      { img: task2card1, correct: false },
      { img: task2card2, correct: true  },
    ],
  },
];

function pickRandomTask2() {
  return ALL_TASK2_DATA[Math.floor(Math.random() * ALL_TASK2_DATA.length)];
}

const ROUND_CONFIG = { totalCards: 5, shuffleSpeed: 1.1, shuffleCount: 4 };

function buildDeck(extraCards) {
  const deck = [
    { id: 0, img: heartCard, isHeart: true },
    ...extraCards.map((img, i) => ({ id: i + 1, img, isHeart: false })),
  ];
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

const SPARKLE_CONFIGS = [
  { x: -130, y: -140, delay: 0.55, size: 24, color: '#ffe066' },
  { x:  120, y: -130, delay: 0.70, size: 18, color: '#ff9d3a' },
  { x: -150, y:   10, delay: 0.80, size: 20, color: '#ffffff' },
  { x:  140, y:   20, delay: 0.62, size: 22, color: '#ffe066' },
  { x:  -70, y:  150, delay: 0.88, size: 16, color: '#ff9d3a' },
  { x:   65, y:  155, delay: 0.68, size: 20, color: '#ffffff' },
  { x:   10, y: -160, delay: 0.75, size: 14, color: '#ffe066' },
  { x: -100, y:  -60, delay: 0.95, size: 12, color: '#ff9d3a' },
  { x:  105, y:  -55, delay: 0.90, size: 14, color: '#ffffff' },
  { x:  160, y:  -80, delay: 1.00, size: 18, color: '#ffe066' },
  { x: -160, y:   90, delay: 0.72, size: 16, color: '#ff9d3a' },
  { x:  110, y:  115, delay: 0.84, size: 22, color: '#ffffff' },
];

function HeartSpotlight({ onDone }) {
  const overlayRef = useRef();
  const cardRef    = useRef();
  const labelRef   = useRef();
  const sparkRefs  = useRef([]);

  useEffect(() => {
    gsap.set(overlayRef.current, { opacity: 0 });
    gsap.set(cardRef.current,    { opacity: 0, scale: 0.35, y: 50 });
    gsap.set(labelRef.current,   { opacity: 0, y: 18 });
    sparkRefs.current.forEach(el => el && gsap.set(el, { opacity: 0, scale: 0 }));

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.to([overlayRef.current, cardRef.current, labelRef.current], {
          opacity: 0, duration: 0.45, ease: 'power2.in', onComplete: onDone,
        });
      },
    });

    tl.to(overlayRef.current, { opacity: 1, duration: 0.35, ease: 'power2.out' })
      .to(cardRef.current, { opacity: 1, scale: 1, y: 0, duration: 0.65, ease: 'back.out(2.2)' }, '-=0.1')
      .to(labelRef.current, { opacity: 1, y: 0, duration: 0.38, ease: 'power2.out' }, '-=0.25');

    SPARKLE_CONFIGS.forEach((s, i) => {
      const el = sparkRefs.current[i];
      if (!el) return;
      tl.to(el, {
        opacity: 1, scale: 1, duration: 0.25, ease: 'back.out(2)',
        onComplete: () => {
          gsap.to(el, {
            opacity: 0, scale: 0.4,
            x: `+=${s.x > 0 ? 20 : -20}`,
            y: '-=35',
            duration: 0.55, ease: 'power2.out',
          });
        },
      }, s.delay);
    });

    tl.to(cardRef.current, { y: -14, duration: 0.75, ease: 'sine.inOut', yoyo: true, repeat: 4 }, '+=0.2')
      .to({}, { duration: 0.3 });
  }, [onDone]);

  return (
    <div ref={overlayRef} className="heart-spotlight-overlay">
      <div className="heart-spotlight-wrap">
        <div ref={cardRef} className="heart-spotlight-card">
          <img src={heartCard} alt="Heart Card" />
          {SPARKLE_CONFIGS.map((s, i) => (
            <div
              key={i}
              ref={el => sparkRefs.current[i] = el}
              className="sparkle-star"
              style={{ left: `calc(50% + ${s.x}px)`, top: `calc(50% + ${s.y}px)`, width: s.size, height: s.size }}
            >
              <svg viewBox="0 0 20 20" width={s.size} height={s.size}>
                <polygon points="10,0 12,8 20,10 12,12 10,20 8,12 0,10 8,8" fill={s.color} />
              </svg>
            </div>
          ))}
        </div>
        <div ref={labelRef} className="heart-spotlight-label">
          This is the Pharaoh's Heart — remember it!
        </div>
      </div>
    </div>
  );
}

function PlayingCard({ card, faceUp, onClick, disabled, glow }) {
  return (
    <div
      className={`play-card ${faceUp ? 'face-up' : ''} ${glow === 'correct' ? 'glow-correct' : glow === 'wrong' ? 'glow-wrong' : ''} ${disabled ? 'card-disabled' : ''}`}
      onClick={() => !disabled && onClick && onClick(card.id)}
      data-card-id={card.id}
    >
      <div className="play-card-inner">
        <div className="play-card-front">
          <img src={card.img} alt="card" />
        </div>
        <div className="play-card-back">
          <img src={cardBack} alt="back" />
        </div>
      </div>
    </div>
  );
}

function Task2({ onCorrect, onWrong, taskData }) {
  const [showWin, setShowWin] = useState(false);
  const answeredRef  = useRef(false);
  const completedRef = useRef(false);
  const overlayRef   = useRef();
  const msgRef       = useRef();
  const fragRef      = useRef();
  const boxRef       = useRef();
  const btnsRef      = useRef([]);

  useEffect(() => {
    const tl = gsap.timeline();
    if (boxRef.current) tl.fromTo(boxRef.current,
      { opacity: 0, y: 40, rotateX: -20 },
      { opacity: 1, y: 0, rotateX: 0, duration: 0.6, ease: 'back.out(1.8)' }
    );
    btnsRef.current.forEach((btn, i) => {
      if (btn) tl.fromTo(btn,
        { opacity: 0, scale: 0, rotation: -15 },
        { opacity: 1, scale: 1, rotation: 0, duration: 0.6, ease: 'elastic.out(1.2, 0.5)' },
        i === 0 ? '-=0.2' : '-=0.35'
      );
    });
  }, []);

  const handleAnswer = useCallback((correct) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    if (correct) setShowWin(true);
    else onWrong();
  }, [onWrong]);

  useEffect(() => {
    if (!showWin || completedRef.current) return;
    completedRef.current = true;
    const tl = gsap.timeline({ onComplete: () => setTimeout(() => onCorrect(), 1200) });
    if (overlayRef.current) tl.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4 });
    if (msgRef.current) tl.fromTo(msgRef.current,
      { opacity: 0, y: -60, scale: 0.75 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2)' }, '-=0.1'
    );
    if (fragRef.current) {
      tl.fromTo(fragRef.current,
        { opacity: 0, scale: 0, rotation: -25, y: 80 },
        { opacity: 1, scale: 1, rotation: 0, y: 0, duration: 0.85, ease: 'elastic.out(1,0.55)' }, '-=0.2'
      );
      tl.to(fragRef.current, { y: -18, duration: 1.1, ease: 'sine.inOut', yoyo: true, repeat: 3 }, '+=0.1');
    }
  }, [showWin, onCorrect]);

  return (
    <div className="task2-overlay">
      {!showWin ? (
        <>
          <div className="task2-question-box" ref={boxRef} style={{ opacity: 0 }}>
            <p className="task2-question-text">{taskData.question}</p>
            <p className="task2-hint-text">{taskData.hint}</p>
          </div>
          <div className="taskk2-answers">
            {taskData.answers.map((ans, i) => (
              <button
                key={i}
                className="taskk2-answer-btn"
                ref={el => btnsRef.current[i] = el}
                style={{ opacity: 0 }}
                onClick={() => handleAnswer(ans.correct)}
              >
                <img src={ans.img} alt="answer" className="task2-stone-img" />
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="task2-win-overlay" ref={overlayRef} style={{ opacity: 0 }}>
          <div className="task2-win-msg" ref={msgRef} style={{ opacity: 0 }}>
            <p>"Remarkable! The Sixth fragment is yours."</p>
          </div>
          <div className="task2-win-fragment" ref={fragRef} style={{ opacity: 0 }}>
            <img src={fragmentImg} alt="Fragment" />
          </div>
        </div>
      )}
    </div>
  );
}

const Level6 = () => {
  const navigate = useNavigate();

  const [extraCards] = useState(() => pickRandomCards(ROUND_CONFIG.totalCards - 1));
  const [task2Data]  = useState(() => pickRandomTask2());
  const [deck, setDeck] = useState(() => buildDeck(pickRandomCards(ROUND_CONFIG.totalCards - 1)));

  const [showPreloader,  setShowPreloader]  = useState(true);
  const [introsDone,     setIntrosDone]     = useState(false);
  const [spotlightDone,  setSpotlightDone]  = useState(false);
  const [phase,          setPhase]          = useState('reveal');
  const [faceUpIds,      setFaceUpIds]      = useState([]);
  const [chooseLeft,     setChooseLeft]     = useState(CHOOSE_SECONDS);
  const [cardGlows,      setCardGlows]      = useState({});
  const [showTask2,      setShowTask2]      = useState(false);
  const [showPause,      setShowPause]      = useState(false);
  const [showWinPopup,   setShowWinPopup]   = useState(false);
  const [showLosePopup,  setShowLosePopup]  = useState(false);

  const deckRef_state  = useRef(deck);
  const phaseRef_state = useRef(phase);

  useEffect(() => { deckRef_state.current = deck; }, [deck]);
  useEffect(() => { phaseRef_state.current = phase; }, [phase]);

  const headerRef    = useRef();
  const hintRef      = useRef();
  const timerBoxRef  = useRef();
  const deckRef      = useRef();
  const task2Ref     = useRef();
  const chooseRef    = useRef(null);
  const pauseRef     = useRef();
  const gameWonRef   = useRef(false);
  const hintBounce   = useRef(null);
  const chosenRef    = useRef(false);
  const shufflingRef = useRef(false);

  const stopAllTimers = useCallback(() => {
    if (chooseRef.current) { clearInterval(chooseRef.current); chooseRef.current = null; }
  }, []);

  const doShuffle = useCallback((currentDeck) => {
    if (shufflingRef.current) return;
    shufflingRef.current = true;

    const cfg = ROUND_CONFIG;
    const container = deckRef.current;
    if (!container) { shufflingRef.current = false; return; }

    const cardEls = Array.from(container.querySelectorAll('.play-card'));
    if (cardEls.length === 0) { shufflingRef.current = false; return; }

    cardEls.forEach(el => gsap.set(el, { x: 0, y: 0, zIndex: 1 }));

    const slotCenters = cardEls.map(el => ({
      x: el.offsetLeft + el.offsetWidth / 2,
      y: el.offsetTop  + el.offsetHeight / 2,
    }));

    const visualSlot = cardEls.map((_, i) => i);
    let logicalOrder = [...currentDeck];
    let count = 0;
    const speed = cfg.shuffleSpeed;

    const doOneSwap = () => {
      if (count >= cfg.shuffleCount) {
        shufflingRef.current = false;
        cardEls.forEach(el => gsap.set(el, { x: 0, y: 0, zIndex: 1 }));
        deckRef_state.current = [...logicalOrder];
        setDeck([...logicalOrder]);
        setPhase('choose');
        startChooseTimer();
        return;
      }
      count++;

      const i = Math.floor(Math.random() * cardEls.length);
      let j = Math.floor(Math.random() * cardEls.length);
      while (j === i) j = Math.floor(Math.random() * cardEls.length);

      const slotI = visualSlot[i];
      const slotJ = visualSlot[j];
      const elI = cardEls[i];
      const elJ = cardEls[j];
      const naturalI = slotCenters[i];
      const naturalJ = slotCenters[j];

      const targetIx = slotCenters[slotJ].x - naturalI.x;
      const targetIy = slotCenters[slotJ].y - naturalI.y;
      const targetJx = slotCenters[slotI].x - naturalJ.x;
      const targetJy = slotCenters[slotI].y - naturalJ.y;

      gsap.to(elI, { x: targetIx, y: targetIy, zIndex: 10, duration: speed, ease: 'power2.inOut' });
      gsap.to(elJ, { x: targetJx, y: targetJy, zIndex: 5,  duration: speed, ease: 'power2.inOut',
        onComplete: () => {
          [visualSlot[i], visualSlot[j]] = [visualSlot[j], visualSlot[i]];
          [logicalOrder[i], logicalOrder[j]] = [logicalOrder[j], logicalOrder[i]];
          setTimeout(doOneSwap, speed * 200);
        }
      });
    };

    doOneSwap();
  }, []);

  const startChooseTimer = useCallback(() => {
    chosenRef.current = false;
    setChooseLeft(CHOOSE_SECONDS);
    if (chooseRef.current) { clearInterval(chooseRef.current); chooseRef.current = null; }
    chooseRef.current = setInterval(() => {
      setChooseLeft(t => {
        if (t <= 1) {
          clearInterval(chooseRef.current);
          chooseRef.current = null;
          if (!chosenRef.current) setShowLosePopup(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  }, []);

  const startReveal = useCallback((currentDeck) => {
    setPhase('reveal');
    setFaceUpIds(currentDeck.map(c => c.id));
    setTimeout(() => {
      setFaceUpIds([]);
      setPhase('shuffle');
      if (hintRef.current) {
        if (hintBounce.current) { hintBounce.current.kill(); hintBounce.current = null; }
        gsap.to(hintRef.current, { y: -60, opacity: 0, duration: 0.45, ease: 'power2.in' });
      }
      setTimeout(() => doShuffle(currentDeck), 1200);
    }, 3500);
  }, [doShuffle]);

  const runIntroAnim = useCallback(() => {
    gsap.set(headerRef.current, { opacity: 0, y: -60 });
    gsap.set(hintRef.current,   { opacity: 0, y: -40, scale: 0.88 });
    gsap.set(deckRef.current,   { opacity: 0, scale: 0.8, y: 60 });

    const tl = gsap.timeline();
    tl.to(headerRef.current, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' })
      .to(hintRef.current, {
        opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2.2)',
        onComplete: () => {
          hintBounce.current = gsap.to(hintRef.current, { y: -8, duration: 0.65, ease: 'sine.inOut', yoyo: true, repeat: -1 });
        },
      }, '-=0.3')
      .to(deckRef.current, { opacity: 1, scale: 1, y: 0, duration: 0.9, ease: 'back.out(1.5)' }, '-=0.3')
      .call(() => startReveal(deckRef_state.current));
  }, [startReveal]);

  useEffect(() => { if (spotlightDone) runIntroAnim(); }, [spotlightDone, runIntroAnim]);

  const handleCardClick = useCallback((cardId) => {
    if (phaseRef_state.current !== 'choose' || chosenRef.current) return;
    chosenRef.current = true;
    clearInterval(chooseRef.current);
    chooseRef.current = null;

    const currentDeck = deckRef_state.current;
    const clickedCard = currentDeck.find(c => c.id === cardId);
    const isCorrect = clickedCard?.isHeart;

    setFaceUpIds(currentDeck.map(c => c.id));

    if (isCorrect) {
      setCardGlows({ [cardId]: 'correct' });
      setTimeout(() => {
        setCardGlows({});
        setFaceUpIds([]);
        gameWonRef.current = true;
        stopAllTimers();
        if (deckRef.current) {
          gsap.to(deckRef.current, {
            opacity: 0, scale: 0.85, y: 40, duration: 0.5, ease: 'power2.in',
            onComplete: () => setShowTask2(true),
          });
        } else {
          setShowTask2(true);
        }
      }, 1400);
    } else {
      setCardGlows({ [cardId]: 'wrong' });
      setTimeout(() => setShowLosePopup(true), 1200);
    }

    if (hintBounce.current) { hintBounce.current.kill(); hintBounce.current = null; }
    if (hintRef.current) gsap.to(hintRef.current, { opacity: 0, duration: 0.3 });
  }, [stopAllTimers]);

  useEffect(() => {
    if (phase !== 'choose' || !timerBoxRef.current) return;
    gsap.fromTo(timerBoxRef.current,
      { opacity: 0, y: -40 },
      { opacity: 1, y: 0, duration: 0.4, ease: 'back.out(1.8)' }
    );
  }, [phase]);

  useEffect(() => {
    if (!showTask2 || !task2Ref.current) return;
    gsap.fromTo(task2Ref.current,
      { opacity: 0, y: '100vh', scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'expo.out' }
    );
  }, [showTask2]);

  useEffect(() => () => stopAllTimers(), [stopAllTimers]);

  pauseRef.current = () => { stopAllTimers(); setShowPause(true); };

  const handleResume = () => {
    setShowPause(false);
    if (!gameWonRef.current && phaseRef_state.current === 'choose') startChooseTimer();
  };

  if (showPreloader) return <Preloader onComplete={() => setShowPreloader(false)} />;

  return (
    <div className="level6-page" style={{ backgroundImage: `url(${level6Bg})` }}>
      {!introsDone && (() => {
        const LevelIntro = require('../components/layout/Levelintro').default;
        return (
          <LevelIntro
            leftImage={leftChar}
            rightImage={rightChar}
            dialogues={DIALOGUES}
            onFinish={() => setIntrosDone(true)}
          />
        );
      })()}

      {introsDone && !spotlightDone && (
        <HeartSpotlight onDone={() => setSpotlightDone(true)} />
      )}

      <div ref={headerRef} style={{ opacity: 0, position: 'relative', zIndex: 100 }}>
        <Header mode="game" onPause={() => pauseRef.current && pauseRef.current()} />
      </div>

      {spotlightDone && !showTask2 && (
        <>
          <div className="hint-box" ref={hintRef} style={{ opacity: 0 }}>
            <p>"Watch the Pharaoh's heart card carefully."</p>
          </div>

          {phase === 'choose' && (
            <div className="choose-timer-styled" ref={timerBoxRef} style={{ opacity: 0 }}>
              <span className={chooseLeft <= 3 ? 'timer-urgent' : ''}>{chooseLeft}s</span>
            </div>
          )}

          <div className="cards-deck" ref={deckRef} style={{ opacity: 0 }}>
            {deck.map(card => (
              <PlayingCard
                key={card.id}
                card={card}
                faceUp={faceUpIds.includes(card.id)}
                onClick={handleCardClick}
                disabled={phase !== 'choose'}
                glow={cardGlows[card.id]}
              />
            ))}
          </div>
        </>
      )}

      {showTask2 && (
        <div ref={task2Ref} style={{ opacity: 0, position: 'relative', zIndex: 50 }}>
          <Task2
            taskData={task2Data}
            onCorrect={() => { unlockNextLevel(6); if (!showWinPopup) setShowWinPopup(true); }}
            onWrong={() => { if (!showLosePopup) setShowLosePopup(true); }}
          />
        </div>
      )}

      {showPause && <PausePopup onResume={handleResume} onLevels={() => navigate('/levels')} onMenu={() => navigate('/mainmenu')} />}
      {showWinPopup && <WinPopup fragmentImg={fragmentImg} onLevels={() => navigate('/levels')} onNext={() => navigate('/levels')} />}
      {showLosePopup && <LosePopup onLevels={() => navigate('/levels')} onReplay={() => navigate(0)} />}
    </div>
  );
};

export default Level6;